import { useState } from 'react';
import { errorIf } from 'utils/errorIf';

export interface Option<T = string> {
  id: string;
  value: T;
}

export const useOptionSelect = <T>({
  options,
  multiSelect = false,
  defaultOptions = [],
}: {
  options: Option<T>[];
  multiSelect?: boolean;
  defaultOptions?: number[];
}) => {
  errorIf(
    defaultOptions.some((o) => o < 0 || o >= options.length),
    `Invalid defaultOptions: '${defaultOptions}'. Please provide valid option numbers from 0 to ${
      options.length - 1
    }.`
  );

  errorIf(
    !multiSelect && defaultOptions.length > 1,
    'Cannot set more than one defaultOption when multiSelect is false.'
  );

  const [selectedIDs, setSelectedIDs] = useState<string[]>(
    defaultOptions?.map((option) => options[option].id) || []
  );

  const onSelectID = (id: string) => {
    const unSelect = !!selectedIDs.find((o) => o === id);
    const option = options.find((o) => o.id === id);
    if (!option) throw new Error(`Invalid option id '${id}' selected.`);

    if (multiSelect) {
      unSelect
        ? setSelectedIDs(selectedIDs.filter((o) => o !== option.id))
        : setSelectedIDs([...selectedIDs, option.id]);
    } else {
      unSelect ? setSelectedIDs([]) : setSelectedIDs([option.id]);
    }
  };

  return {
    selectedIDs,
    onSelectID,
    setSelectedIDs,
  };
};
